import React from 'react';
import {
  Button,
  ButtonProps,
  CircularProgress,
  Box,
  LinearProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';

const StyledLoadingButton = styled(Button)<{
  loading?: boolean;
  loadingPosition?: 'start' | 'end' | 'center';
}>(({ theme, loading, loadingPosition = 'center' }) => ({
  position: 'relative',
  ...(loading && {
    pointerEvents: 'none',
    ...(loadingPosition === 'center' && {
      '& .MuiButton-startIcon, & .MuiButton-endIcon': {
        opacity: 0,
      },
    }),
  }),
}));

const LoadingIndicator = styled(Box)<{
  loadingPosition: 'start' | 'end' | 'center';
  size: number;
}>(({ theme, loadingPosition, size }) => ({
  position: loadingPosition === 'center' ? 'absolute' : 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  ...(loadingPosition === 'center' && {
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
  }),
  ...(loadingPosition === 'start' && {
    marginRight: theme.spacing(1),
  }),
  ...(loadingPosition === 'end' && {
    marginLeft: theme.spacing(1),
  }),
  '& .MuiCircularProgress-root': {
    width: `${size}px !important`,
    height: `${size}px !important`,
  },
}));

const ProgressOverlay = styled(LinearProgress)(({ theme }) => ({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  height: 2,
  borderRadius: `0 0 ${theme.shape.borderRadius}px ${theme.shape.borderRadius}px`,
}));

export interface LoadingButtonProps extends Omit<ButtonProps, 'startIcon' | 'endIcon'> {
  /**
   * Whether button is in loading state
   */
  loading?: boolean;
  /**
   * Position of loading indicator
   */
  loadingPosition?: 'start' | 'end' | 'center';
  /**
   * Loading indicator type
   */
  loadingIndicator?: 'circular' | 'linear' | 'dots' | React.ReactNode;
  /**
   * Size of loading indicator
   */
  loadingSize?: number;
  /**
   * Color of loading indicator
   */
  loadingColor?: 'primary' | 'secondary' | 'inherit';
  /**
   * Text to show when loading
   */
  loadingText?: string;
  /**
   * Whether to show progress percentage (for linear indicator)
   */
  showProgress?: boolean;
  /**
   * Progress value (0-100)
   */
  progress?: number;
  /**
   * Start icon when not loading
   */
  startIcon?: React.ReactNode;
  /**
   * End icon when not loading
   */
  endIcon?: React.ReactNode;
  /**
   * Whether to disable button interactions when loading
   */
  disableOnLoading?: boolean;
  /**
   * Custom loading component
   */
  customLoadingComponent?: React.ReactNode;
}

const DotsIndicator: React.FC<{ color?: string }> = ({ color = 'currentColor' }) => (
  <Box
    sx={{
      display: 'flex',
      gap: 0.5,
      '& .dot': {
        width: 4,
        height: 4,
        borderRadius: '50%',
        backgroundColor: color,
        animation: 'loadingDots 1.4s infinite ease-in-out',
        '&:nth-of-type(1)': { animationDelay: '-0.32s' },
        '&:nth-of-type(2)': { animationDelay: '-0.16s' },
      },
      '@keyframes loadingDots': {
        '0%, 80%, 100%': {
          transform: 'scale(0)',
        },
        '40%': {
          transform: 'scale(1)',
        },
      },
    }}
  >
    <Box className="dot" />
    <Box className="dot" />
    <Box className="dot" />
  </Box>
);

export const LoadingButton: React.FC<LoadingButtonProps> = ({
  loading = false,
  loadingPosition = 'center',
  loadingIndicator = 'circular',
  loadingSize = 16,
  loadingColor = 'inherit',
  loadingText,
  showProgress = false,
  progress = 0,
  startIcon,
  endIcon,
  disableOnLoading = true,
  customLoadingComponent,
  children,
  disabled,
  onClick,
  ...props
}) => {
  const isDisabled = disabled || (loading && disableOnLoading);
  const buttonText = loading && loadingText ? loadingText : children;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!loading && onClick) {
      onClick(event);
    }
  };

  const renderLoadingIndicator = () => {
    if (customLoadingComponent) {
      return customLoadingComponent;
    }

    switch (loadingIndicator) {
      case 'circular':
        return (
          <CircularProgress
            size={loadingSize}
            color={loadingColor}
            thickness={4}
          />
        );
      case 'linear':
        return (
          <ProgressOverlay
            variant={showProgress ? 'determinate' : 'indeterminate'}
            value={showProgress ? progress : undefined}
            color={loadingColor}
          />
        );
      case 'dots':
        return <DotsIndicator />;
      default:
        if (React.isValidElement(loadingIndicator)) {
          return loadingIndicator;
        }
        return (
          <CircularProgress
            size={loadingSize}
            color={loadingColor}
            thickness={4}
          />
        );
    }
  };

  const renderContent = () => {
    if (loadingIndicator === 'linear') {
      return (
        <>
          {buttonText}
          {loading && renderLoadingIndicator()}
        </>
      );
    }

    const indicator = loading && (
      <LoadingIndicator loadingPosition={loadingPosition} size={loadingSize}>
        {renderLoadingIndicator()}
      </LoadingIndicator>
    );

    if (loadingPosition === 'start') {
      return (
        <>
          {indicator}
          {buttonText}
        </>
      );
    }

    if (loadingPosition === 'end') {
      return (
        <>
          {buttonText}
          {indicator}
        </>
      );
    }

    // Center position
    return (
      <>
        {buttonText}
        {indicator}
      </>
    );
  };

  return (
    <StyledLoadingButton
      loading={loading}
      loadingPosition={loadingPosition}
      disabled={isDisabled}
      onClick={handleClick}
      startIcon={!loading ? startIcon : undefined}
      endIcon={!loading ? endIcon : undefined}
      {...props}
    >
      {renderContent()}
    </StyledLoadingButton>
  );
}; 